import type {
    TimeZoneEnumListEntry,
    TimeZoneEnumListType,
} from '@/Shared/AutoGenerated/TimeZoneEnum'

export enum TimeslotTimeFormat {
    Format24Hours = 'Format24Hours',
    Format12Hours = 'Format12Hours',
}

export default function useDate() {
    function getCurrentDate(): string {
        const currentDate = new Date()

        return currentDate.toISOString().substring(0, 10)
    }

    function getCurrentYear(): number {
        return new Date().getFullYear()
    }

    function getCurrentMonthAndYear(): string {
        return Intl.DateTimeFormat('en-US', {
            month: 'long',
            year: 'numeric',
            timeZone: getBrowserTimeZone(),
        }).format(new Date())
    }

    function getBrowserTimeZone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone
    }

    interface TimezoneWithFallbackResult {
        timezoneEntry: TimeZoneEnumListEntry
        isUsingFallback: boolean
        browserTimezone: string
        fallbackTimezone: string
    }

    function getBrowserTimezoneWithFallback(
        timezones: TimeZoneEnumListType,
        fallbackTimezone: string,
    ): TimezoneWithFallbackResult {
        const browserTimeZone = getBrowserTimeZone()

        const entry = timezones.find((timeZone: TimeZoneEnumListEntry) => {
            return timeZone.key === browserTimeZone
        })

        if (entry) {
            return {
                timezoneEntry: entry,
                isUsingFallback: false,
                browserTimezone: browserTimeZone,
                fallbackTimezone: fallbackTimezone,
            }
        }

        const fallbackEntry = timezones.find(
            (timeZone: TimeZoneEnumListEntry) => {
                return timeZone.key === fallbackTimezone
            },
        )

        if (!fallbackEntry) {
            throw new Error(
                `Timezone not found for browser timezone ${browserTimeZone} nor fallback ${fallbackTimezone}`,
            )
        }

        return {
            timezoneEntry: fallbackEntry,
            isUsingFallback: true,
            browserTimezone: browserTimeZone,
            fallbackTimezone: fallbackTimezone,
        }
    }

    function dateTime(
        value: string,
        targetTimeZone: string | null = null,
    ): string {
        const timeZone = targetTimeZone ?? getBrowserTimeZone()
        const startAt = new Date(value)

        return Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            dateStyle: 'long',
            timeStyle: 'short',
        }).format(startAt)
    }

    function date(value: string, targetTimeZone: string | null = null): string {
        const timeZone = targetTimeZone ?? getBrowserTimeZone()
        const date = new Date(value)

        return Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            dateStyle: 'long',
            // timeStyle: 'short',
        }).format(date)
    }

    function time(value: string, targetTimeZone: string | null = null): string {
        const timeZone = targetTimeZone ?? getBrowserTimeZone()
        const date = new Date(value)

        return Intl.DateTimeFormat('en-US', {
            timeZone: timeZone,
            // dateStyle: 'long',
            timeStyle: 'short',
        }).format(date)
    }

    function timeAs12hours(value: string): string {
        return new Date(`2000-01-01 ${value}:00`).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            hour12: true,
        })
    }

    function timeAs24hours(value: string): string {
        return new Date(`2000-01-01 ${value}:00`).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: undefined,
            hour12: false,
        })
    }

    function timeslot(value: string, format: TimeslotTimeFormat): string {
        if (format === TimeslotTimeFormat.Format12Hours) {
            return timeAs12hours(value)
        }

        if (format === TimeslotTimeFormat.Format24Hours) {
            return timeAs24hours(value)
        }

        throw new Error(`Unknown timeslot time format`)
    }

    return {
        getCurrentDate,
        getCurrentYear,
        getCurrentMonthAndYear,
        getBrowserTimeZone,
        getBrowserTimezoneWithFallback,
        dateTime,
        date,
        time,
        timeslot,
    }
}
